import { CommonModule } from '@angular/common';
import { Component, ElementRef, inject, Inject, signal, ViewChild } from '@angular/core';
import { ReactiveFormsModule, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { CountryService } from '@services/country.service';
import { PagesService } from '@services/pages.service';
import { SharedService } from '@services/shared.service';
import { AuthService } from 'src/app/Auth/AuthService';
import { environment } from '@env';
declare const google: any;

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [RouterLink, ReactiveFormsModule, CommonModule, TranslateModule],
  providers: [],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss', '../../../../auth.scss'],
})
export class LoginComponent {
  baseUrl = environment.baseUrl;

  LoginForm: FormGroup;
  pageService = inject(PagesService);
  countryService = inject(CountryService);
  isPasswordVisible = false;

  @ViewChild('googleBtn', { static: false }) googleBtnRef!: ElementRef;

  ngOnInit() {
    // Hide loader when login page loads (after logout)
    this.sharedService.hideLoader();
  }

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private sharedService: SharedService
  ) {
    this.markPaymentAsChecked()
    this.LoginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]],
    });
  }

onSubmit() {
  this.sharedService.showLoader();

  if (this.LoginForm.valid) {
    this.authService.login('LOGIN', this.LoginForm.value).subscribe({
      next: (response: any) => {
        const user = response.user;
        this.authService.setToken(response);

        // ✅ Fetch user company + image
        this.pageService.getAll('GET_USER_COMPANY').subscribe({
          next: (res: any) => {
            const userData = res[0] || {};
            const imgUrl =
              userData.business_logo?.[0]?.url || 'assets/images/profile.png';
            localStorage.setItem('profileImage', imgUrl);
            this.sharedService.updateProfileImage(imgUrl);
          },
          error: () => {
            this.sharedService.updateProfileImage('assets/images/profile.png');
          }
        });

        // ✅ Company check
        if (!user.isCompany || user.isCompany.length === 0) {
          this.sharedService.createNotification(
            'warning',
            "You haven't added a company yet."
          );
          this.sharedService.hideLoader();
          this.router.navigate(['/auth/company']);
          return;
        }

        // ✅ Email verification check
        if (!user.verified) {
          this.sharedService.createNotification(
            'warning',
            'Please verify your account.'
          );
          this.sharedService.hideLoader();
          this.router.navigate(['/auth/send-otp']);
          return;
        }

        // ✅ Success: Redirect to home
        // Note: Don't hide loader here - let layout component hide it after it loads
        this.router.navigate(['/home/quick-actions']);
      },

      error: (err) => {
        this.sharedService.hideLoader();

        // ✅ Extract backend message directly
        const backendMessage =
          err?.error?.message ||
          err?.message ||
          'Login failed. Please try again.';

        // ✅ Handle incomplete signup case
        if (
          err?.error?.code === 'ERROR_CODE_ACCESS_DENIED' &&
          backendMessage.includes('complete all steps')
        ) {
          this.sharedService.createNotification('warning', backendMessage);
          setTimeout(() => this.router.navigate(['/auth/signup']), 100);
          return;
        }

        // ✅ Auth related (401 / 403)
        if (err?.status === 401 || err?.status === 403) {
          this.sharedService.createNotification('error', backendMessage);
          return;
        }

        // ✅ All other errors
        this.sharedService.createNotification('error', backendMessage);
      }
    });
  } else {
    this.LoginForm.markAllAsTouched();
    this.sharedService.hideLoader();
  }
}

  markPaymentAsChecked() {
    this.route.queryParams.subscribe((params) => {
      const sessionId = params['session_id'];
      // ✅ Remove session_id from the URL hash
      const baseHash = window.location.hash.split('?')[0]; // Keep part before '?'
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${baseHash}`
      );
      if (sessionId) {
        const data = { session_id: sessionId };
        this.pageService.update('CHECK_PAYMENT', data).subscribe({
          next: (response: any) => {
            if (
              !response.data?.isSuccess &&
              response.data?.isSuccess !== undefined
            ) {
              return;
            }
            this.sharedService.createNotification('success', response.data?.status);

          },
        });
      }
    });

  }


  togglePasswordVisibility(): void {
    this.isPasswordVisible = !this.isPasswordVisible;
  }



  loginWithFacebook() {
    this.authService
      .signInWithFacebook()
      .then((res) => console.log(res.user))
      .catch((err) => console.error(err));
  }

  private decodeToken(token: string) {
    return JSON.parse(atob(token.split('.')[1]));
  }
  async handleLogin(res: any) {
    if (res) {
      const user = res.user;
      const provider = user.providerData?.[0] || {};

      const obj = {
        name: user.displayName, // ✅ Clean, because it's available
        email: user.email,
        phone_number:
          user.phoneNumber || user.providerData?.[0]?.phoneNumber || '',
        policy: true,
        country: await this.countryService.getUserCountry(),
        type: 'google',
      };

      this.authService.login('SOCIAL_LOGIN', obj).subscribe(
        (response: any) => {
          if (response.success) {
            this.authService.setTokenII(response);
            this.router.navigate(['/home/quick-actions']);
          } else {
            this.sharedService.createNotification('error', response?.message);
          }
        },
        (error) => {
          console.error('Login failed', error);
        }
      );
    }
  }

  ngAfterViewInit(): void {
    // initialize Google Identity Services
    google.accounts.id.initialize({
      // client_id: '1024763660357-cbfi1rq4bto3ii62o4kt6ervdpbu957f.apps.googleusercontent.com',
      client_id: '423912041942-nlq84oq2airo505dl11ckg4jva5666cn.apps.googleusercontent.com',
      callback: (response: any) => this.handleGoogleResponse(response)
    });

    // render the button (use nativeElement if using #googleBtn)
    const btnEl = this.googleBtnRef?.nativeElement || document.getElementById('googleBtn');
    if (btnEl) {
      google.accounts.id.renderButton(btnEl, { theme: 'outline', size: 'large' });
    }

    // optional: show One Tap prompt (if you want)
    // google.accounts.id.prompt();
  }

  handleGoogleResponse(response: any) {
    const idToken = response?.credential;
    if (!idToken) {
      console.error('No Google credential received', response);
      return;
    }

    // show loader
    this.sharedService.showLoader();

    // **Important**: backend example you showed expects { access_token: "" }
    // If your backend expects { token: ... } change below accordingly.
    const payload = { access_token: idToken };

    this.authService.login('SOCIAL_LOGIN', payload).subscribe({
      next: (res: any) => {
        this.sharedService.hideLoader();

        // handle different possible response shapes
        if (res?.success || res?.authToken || res?.data) {
          // try common keys, change depending on your backend
          // If backend returns { authToken: "...", data: {...} } use setTokenII
          if (res.authToken || res.data?.authToken) {
            this.authService.setTokenII(res);
          } else if (res.token || res.data?.token) {
            // fallback - store token manually
            this.authService.setTokenII({
              authToken: res.token || res.data.token,
              data: { user: res.data?.user || res.user || {} }
            });
          } else if (res.user) {
            // old shape: response.user.authToken etc.
            this.authService.setToken(res);
          } else {
            console.warn('Unexpected social login response shape', res);
          }

          // navigate
          this.router.navigate(['/auth/registered-success']);
        } else {
          // backend signalled failure
          this.sharedService.createNotification('error', res?.message || 'Google login failed');
        }
      },
      error: (err) => {
        this.sharedService.hideLoader();
        console.error('Google login error', err);
        this.sharedService.createNotification('error', 'Server error during social login');
      }
    });
  }
  // optional method to trigger prompt manually
  loginWithGoogle() {
    google.accounts.id.prompt();
  }

}
